import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Lightweight first-run product tour. Fires only when `localStorage.bp_show_tour`
 * is set (Onboarding writes it after launching the first pass). The tour
 * highlights each sidebar item in turn by looking up its `data-testid` and
 * drawing a spotlight ring around it — no external tour library, no dependency
 * on router state, so it works even if the merchant navigates mid-tour.
 */
const STEPS = [
  { testId: "nav-overview", title: "This is your dashboard", body: "Live counts of every pass, install, return visit and reward — all in one place. Come here every morning." },
  { testId: "nav-programs", title: "Programs", body: "Edit any pass — colours, cover photo, the back-of-pass fields. Every change updates on your customers' phones instantly." },
  { testId: "nav-scanning", title: "Scanning", body: "Your staff PIN lives here. Staff scan a customer's pass with any phone camera, enter the PIN, and the visit or reward is recorded instantly." },
  { testId: "nav-customers", title: "Customers", body: "Every pass holder, their balance, when they last came in. Filter by 'reward available' to know exactly who's due for a free coffee." },
  { testId: "nav-campaigns", title: "Campaigns", body: "Push a lock-screen offer to lapsed regulars or reward your top spenders. No app open needed — the pass they already have does the work." },
  { testId: "nav-analytics", title: "Analytics", body: "The scan → install → return-visit funnel + hourly heatmap. Know exactly what makes your regulars come back." },
];

export function FirstRunTour() {
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    try {
      if (localStorage.getItem("bp_show_tour") === "1") {
        // Give the layout a beat to render the sidebar before spotlighting.
        const t = setTimeout(() => setActive(true), 350);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!active) return;
    const compute = () => {
      const el = document.querySelector(`[data-testid='${STEPS[i].testId}']`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => { window.removeEventListener("resize", compute); window.removeEventListener("scroll", compute, true); };
  }, [i, active]);

  const finish = () => {
    try { localStorage.setItem("bp_show_tour", "0"); } catch (_) {}
    setActive(false);
  };

  if (!active) return null;
  const step = STEPS[i];
  const isFirst = i === 0;
  const isLast = i === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[95] pointer-events-none" data-testid="tour-overlay">
        {/* Dim backdrop that DOES receive clicks (blocks the app until dismissed) */}
        <div className="absolute inset-0 bg-[#0B0D10]/62 backdrop-blur-[2px] pointer-events-auto" onClick={finish} />

        {/* Spotlight ring around the active sidebar item */}
        {rect && (
          <motion.div key={`ring-${i}`} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="absolute rounded-2xl border-2 border-[#C8FF4D] shadow-[0_0_0_9999px_rgba(11,13,16,0.62)]"
            style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }} />
        )}

        {/* Tour tooltip card — anchored beside the highlight when possible, else centered */}
        <motion.div key={`tip-${i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}
          className="absolute pointer-events-auto rounded-3xl bg-white border border-[#E8E5DE] shadow-[0_30px_60px_-20px_rgba(11,13,16,0.5)] w-[340px] p-6"
          style={{
            top: rect ? Math.min(Math.max(rect.top + rect.height / 2 - 90, 24), window.innerHeight - 240) : "50%",
            left: rect ? Math.min(rect.left + rect.width + 24, window.innerWidth - 360) : "50%",
            transform: rect ? undefined : "translate(-50%, -50%)",
          }}
          data-testid={`tour-step-${step.testId}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#0B0D10] bg-[#C8FF4D] rounded-full px-2 py-0.5 font-bold">
              <Sparkles size={10} strokeWidth={2.4} /> Quick tour · {i + 1}/{STEPS.length}
            </span>
            <button onClick={finish} data-testid="tour-skip" className="h-7 w-7 grid place-items-center rounded-full text-[#25282E]/45 hover:text-[#0B0D10] hover:bg-[#F0EDE5] transition-colors"><X size={13} /></button>
          </div>
          <h3 className="font-display font-bold text-[19px] tracking-[-0.01em]">{step.title}</h3>
          <p className="text-[14px] text-[#25282E]/70 mt-2 leading-relaxed">{step.body}</p>
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {STEPS.map((_, idx) => (
                <span key={idx} className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-[#0B0D10]" : idx < i ? "w-3 bg-[#0B0D10]/40" : "w-3 bg-[#0B0D10]/12"}`} />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {!isFirst && (
                <button onClick={() => setI(i - 1)} data-testid="tour-prev"
                  className="h-9 w-9 rounded-full border border-[#E8E5DE] hover:border-[#0B0D10] grid place-items-center transition-colors"><ArrowLeft size={14} /></button>
              )}
              <button onClick={() => isLast ? finish() : setI(i + 1)} data-testid={isLast ? "tour-finish" : "tour-next"}
                className="btn-lift h-9 rounded-full bg-[#0B0D10] text-white px-4 text-[13px] font-semibold inline-flex items-center gap-1.5 hover:bg-black">
                {isLast ? "Got it" : "Next"} <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
